import React, { useEffect, useRef, useState, useContext } from 'react';
import { Accordion, Dropdown, AccordionContext } from 'react-bootstrap';
import OurLink from '../../elements/OurLink';
import { getDateAndTime } from '../../../lib/utils';
import {
  Circle,
  Delete,
  OpenInNew,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import ConfirmationModal from '../../elements/ConfirmationModal';
import { deleteExecution } from '../../../modules/executions';
import { Avatar, Typography } from '@mui/material';
import { updateTestCaseAssociations } from '../../../modules/testcycles';
import { isAdminUser, isExecutiveManager, showUserDisplayName } from '../../../lib/userUtils';

const TestExecutionAccordionHeader = ({
  execution,
  data,
  statuses,
  selectedStatus,
  defaultStatus,
  onDropdownChange,
  timer,
  elapsedTime,
  onTimerClick,
  formatElapsedTime,
  fetchSelectedExecutionDetails,
  currentExecution,
  addNotification,
  executionId,
  singlePage,
  isTestCycleCompleted,
  fetchExecutionData,
  users,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAssignWarning, setShowAssignWarning] = useState(false);
  const [assignWarningMessage, setAssignWarningMessage] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const association = execution?.association || currentExecution?.association;
  const assignedTo = execution?.executed_by || currentExecution?.executed_by;
  const isAssignedToMe = assignedTo === currentUser?.id;

  const { activeEventKey } = useContext(AccordionContext);
  const isAccordionOpen = activeEventKey === execution?.id;
  const isExecutionEditable =
    data?.status_details?.name === 'Approved' &&
    (execution?.is_editable || currentExecution?.is_editable);

  const currentStatus =
    statuses?.find((status) => status.id === selectedStatus) || defaultStatus;

  const handleToggle = (id) => {
    setOpenDropdownId((prevId) => (prevId === id ? null : id));
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpenDropdownId(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteExecution = async () => {
    try {
      await deleteExecution(execution?.id || executionId);
      addNotification('success', 'Execution deleted successfully!');
      setShowConfirm(false);
      fetchExecutionData();
    } catch (error) {
      addNotification(
        'danger',
        error?.message || 'Failed to delete execution. Please try again.'
      );
    }
  };

  const handleAssignToMe = async (skip = false) => {
    try {
      const response = await updateTestCaseAssociations(association?.id, executionId, skip);
       if (response?.warning) {
        setAssignWarningMessage(response.message);
        setShowAssignWarning(true);
        return;
      }
      if (!singlePage) {
        addNotification('success', 'Test case assigned to you successfully!');
        fetchSelectedExecutionDetails();
        fetchExecutionData(1, true);
      } else {
        window.location.reload();
      }
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to assign.');
    }
  };
   const handleConfirmAssign = () => {
    setShowAssignWarning(false);
    handleAssignToMe(true);
  };

  const openNewTab = () => {
    const url = `/project/${data?.gainss_project_id}/testexecution/${currentExecution?.key}?testcycle=${currentExecution?.testcycle?.key}&association=${data?.key}`;
    window.open(url, '_blank');
  };

  const renderSummary = () =>
    data?.summary?.length > 50
      ? `${data.summary.substring(0, 50)} ...`
      : data.summary;

  const renderStatusDropdown = () => (
    <div className="d-flex align-items-center">
      <div ref={dropdownRef}>
        <Dropdown
          id={execution?.id}
          autoClose="outside"
          show={openDropdownId === execution?.id}
          onClick={(e) => {
            e.stopPropagation();
            handleToggle(execution?.id);
          }}>
          <Dropdown.Toggle
            as="div"
            role="button"
            className="d-flex align-items-center"
            aria-label="Status Dropdown"
            style={{
              backgroundColor: currentStatus?.color || '#6c757d',
              color: '#fff',
              padding: '0.5rem 1rem',
            }}>
            {currentStatus?.name || 'Select Status'}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {statuses?.map((status) => (
              <Dropdown.Item
                key={status.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onDropdownChange(status.id);
                  setOpenDropdownId(null);
                }}
                className={`d-flex align-items-center ${
                  status.id === currentStatus?.id ? 'selected' : ''
                }`}>
                <Circle sx={{ color: status?.color, marginRight: '10px' }} />
                {status?.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div
        className="d-flex align-items-center justify-content-center gap-2 p-2"
        style={{
          marginLeft: '10px',
          border: '1px solid #ddd',
          borderRadius: '5px',
          width: '120px',
        }}>
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onTimerClick();
          }}
          onKeyDown={(e) => ['Enter', ' '].includes(e.key) && onTimerClick()}
          aria-label={timer ? 'Stop Timer' : 'Start Timer'}
          style={{
            boxShadow: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}>
          {timer ? (
            <Pause titleAccess="Stop Timer" />
          ) : (
            <PlayArrow titleAccess="Start Timer" />
          )}
        </div>
        <div style={{ fontSize: '0.9em', paddingTop: '6px' }}>
          {formatElapsedTime(elapsedTime)}
        </div>
      </div>
    </div>
  );

  const renderReadOnlyActions = () => (
    <div className="d-flex align-items-center gap-2">
      {!isExecutiveManager() && !isAssignedToMe && isExecutionEditable && (
        <Typography
          as="span"
          onClick={(e) => {
          if (isAccordionOpen) {
            e.stopPropagation();
          }handleAssignToMe();
          }}
          sx={{
            marginLeft: '15px',
            fontSize: '12px',
            color: '#1b3f87',
            padding: '5px 16px',
            border: '1px solid #1b3f87',
            borderRadius: '4px',
            '&:hover': { textDecoration: 'underline' },
          }}>
          Assign to me
        </Typography>
      )}
      <div
        style={{
          backgroundColor:
            currentExecution?.status?.color || execution?.status?.color || '',
          color: '#fff',
          padding: '6px 18px',
          borderRadius: '4px',
          textAlign: 'center',
        }}>
        {currentExecution?.status?.name || execution?.status?.name || ''}
      </div>
    </div>
  );

  return (
    <>
      <Accordion.Header
        className={`mt-3 ${singlePage ? 'custom-no-arrow' : ''}`}>
        <div
          className="d-flex align-items-center justify-content-between w-100"
          style={{ padding: '0 15px 0 0' }}>
          <div className="d-flex">
            {!singlePage && (
              <div
                title="Open execution in new tab"
                className="p-2 text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openNewTab();
                }}>
                <OpenInNew />
              </div>
            )}
            <div>
              <OurLink
                to={`/project/${data?.gainss_project_id}/testcases/${data?.key}`}>
                <strong>{data?.key}</strong>
                {data?.module_details?.name
                  ? ` - ${data.module_details.name}`
                  : ''}
              </OurLink>
              <div className="accordion-header-text">
                <span title={data?.summary} className="ellipsis">
                  {renderSummary()}
                </span>
              </div>
              <div className="accordion-header-text">
                Executed On:{' '}
                {getDateAndTime(
                  execution?.updated_at || currentExecution?.updated_at
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Avatar
              sx={{ width: 30, height: 30, fontSize: '1rem' }}
              title={showUserDisplayName(assignedTo, users) || 'Unassigned'}
            >
              {showUserDisplayName(assignedTo, users)?.[0]}
            </Avatar>
            {isExecutionEditable && isAssignedToMe
              ? renderStatusDropdown()
              : renderReadOnlyActions()}
            {!singlePage && !isTestCycleCompleted && isAdminUser() && (
              <div
                role="button"
                tabIndex={0}
                title="Remove execution"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirm(true);
                }}
                style={{
                  boxShadow: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}>
                <Delete sx={{ color: 'red' }} />
              </div>
            )}
          </div>
        </div>
      </Accordion.Header>
      <ConfirmationModal
        show={showConfirm}
        handleConfirm={handleDeleteExecution}
        handleClose={() => setShowConfirm(false)}
        title="Remove Execution"
        body={
          <span className="text-danger">
            Are you sure you want to remove execution for{' '}
            <strong>{data?.key}</strong> from this iteration? This action cannot
            be undone.
          </span>
        }
      />
        <ConfirmationModal
        show={showAssignWarning}
        handleConfirm={handleConfirmAssign}
        handleClose={() => setShowAssignWarning(false)}
        title="Confirm Assignment"
        body={assignWarningMessage}
        buttonLabel="Assign Anyway"
        buttonStyle="primary"
      />
    </>
  );
};

export default TestExecutionAccordionHeader;
